import { useEffect, useState } from "react";
import * as Y from "yjs";
import { useYDoc } from "../state/yjs-provider";
import { BlockNoteEditor } from "./editor/blocknote-editor";

/** Well-known ID for the document root node in the tree */
const DOC_ROOT_ID = "__document_root__";

function syncDocNode(doc: Y.Doc, title: string) {
  const nodesMap = doc.getMap("nodes");
  const existing = nodesMap.get(DOC_ROOT_ID) as Y.Map<unknown> | undefined;
  if (existing) {
    if (existing.get("name") !== (title || "Untitled")) {
      existing.set("name", title || "Untitled");
    }
  } else {
    const nodeMap = new Y.Map<unknown>();
    nodeMap.set("id", DOC_ROOT_ID);
    nodeMap.set("type", "document");
    nodeMap.set("name", title || "Untitled");
    nodeMap.set("_syncSource", "document-title");
    nodesMap.set(DOC_ROOT_ID, nodeMap);
  }
}

export function DocumentView() {
  const doc = useYDoc();
  const metaMap = doc.getMap("documentMeta");

  const [title, setTitle] = useState(
    () => (metaMap.get("title") as string) || "",
  );

  useEffect(() => {
    const observer = () => setTitle((metaMap.get("title") as string) || "");
    metaMap.observe(observer);
    return () => metaMap.unobserve(observer);
  }, [metaMap]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTitle(val);
    metaMap.set("title", val);

    // Mirror title into nodesMap so tree view shows the document
    syncDocNode(doc, val);
  };

  // Keep tree in sync when title changes from remote collaborators
  useEffect(() => {
    syncDocNode(doc, title);
  }, [title, doc]);

  return (
    <div className="flex w-full flex-col h-full bg-[var(--color-bg-document)]">
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-[60ch] max-w-[90vw] py-6">
          {/* Editable title — collaborative via Yjs */}
          <input
            value={title}
            onChange={handleTitleChange}
            placeholder="Untitled"
            className="w-full bg-transparent text-lg font-bold text-white/90 placeholder:text-white/20 outline-none border-none mb-1 px-0"
          />
          <div className="border-b border-white/5 mb-4" />

          <BlockNoteEditor />
        </div>
      </div>
    </div>
  );
}
